/// <reference types="./types" />

import { ConditionGroupOperator } from './ConditionGroupOperator';
import { ConditionOperator } from './ConditionOperator';

/**
 * Converts the specified report editor UI filters to a condition group for the API.
 * 
 * @param {unknown[]} filters The filters to convert.
 * 
 * @returns The converted condition group.
 */
export function uiFiltersToAPIFilters(filters) {
    const rootGroup = {
        conditions: [],
        count: 0,
        groups: [],
        operator: ConditionGroupOperator.Or,
        order: 0
    };

    if (!filters.length) {
        return rootGroup;
    }

    let subgroup;

    if (filters[1] && filters[1].type === 'and') {
        subgroup = createSubgroup(rootGroup);
    }

    let filter = filters[0];

    addFilterToGroup(filter, subgroup ?? rootGroup);

    for (let i = 1; i < filters.length; i++) {
        const next = filters[i + 1];

        filter = filters[i];

        if (filter.type === 'and') {
            addFilterToGroup(filter, subgroup ?? rootGroup);
        } else if (next) {
            if (next.type === 'and') {
                subgroup = createSubgroup(rootGroup);

                addFilterToGroup(filter, subgroup);
            } else {
                subgroup = undefined;

                addFilterToGroup(filter, rootGroup);
            }
        } else {
            subgroup = undefined;

            addFilterToGroup(filter, rootGroup);
        }
    }

    return unwrap(rootGroup);
}

/**
 * Adds the specified report editor UI filter to the specified condition group.
 * 
 * @param {unknown} filter The filter to add.
 * @param {ConditionGroup} group The group to which the filter will be added.
 */
function addFilterToGroup(filter, group) {
    const { conditions, count, groups } = group;

    if (isList(filter)) {
        groups.push(listOfValuesFilterToGroup(filter, count));
    } else {
        conditions.push(filterToCondition(filter, count));
    }

    group.count++;
}

/**
 * Creates a new group and adds it as a child of the specified group.
 * 
 * @param {ConditionGroup} parent 
 * 
 * @returns The new group.
 */
function createSubgroup(parent) {
    const group = {
        conditions: [],
        count: 0,
        groups: [],
        operator: ConditionGroupOperator.And,
        order: 0
    };

    parent.groups.push(group);

    parent.count++;

    return group;
}

/**
 * Converts the specified report editor UI filter to a condition with the specified order.
 * 
 * @param {unknown} filter The filter to convert.
 * @param {number} order The condition's order within its group.
 * 
 * @returns The new condition.
 */
function filterToCondition(filter, order) {
    const { columnId, value } = filter;

    return {
        column: columnId,
        operator: getAPIOperator(filter),
        order,
        value
    };
}

/**
 * Retrieves the API condition operator for the specified filter editor UI filter.
 * 
 * @param {{operator: string}} filter The filter whose API operator will be retrieved.
 * 
 * @throws The filter's operator is not a recognized value.
 */
function getAPIOperator({ operator }) {
    switch (operator) {
        case 'contains':
            return ConditionOperator.Contains;
        case 'doesnotcontain':
            return ConditionOperator.NotContains;
        case 'endswith':
            return ConditionOperator.EndsWith;
        case 'eq':
            return ConditionOperator.Equal;
        case 'ge':
            return ConditionOperator.GreaterThanOrEqual;
        case 'gt':
            return ConditionOperator.GreaterThan;
        case 'le':
            return ConditionOperator.LessThanOrEqual;
        case 'lt':
            return ConditionOperator.LessThan;
        case 'ne':
            return ConditionOperator.NotEqual;
        case 'startswith':
            return ConditionOperator.StartsWith;
    }

    throw new Error(
        `Could not get the UI operator for the condition because it has an invalid operator (${operator}).`
    );
}

/**
 * Retrieves the appropriate condition operator for the specified list-of-values report editor UI filter.
 * 
 * @param {{operator: string}} filter The list-of-values filter whose condition operator will be retrieved.
 * 
 * @returns The appropriate condition operator for the list-of-values filter.
 */
function getListConditionOperator({ operator }) {
    switch (operator) {
        case 'list':
            return ConditionOperator.Equal;
        case 'nlist':
            return ConditionOperator.NotEqual;
    }

    throw new Error(
        'Could not determine the condition operator for the filter because it is not a list-of-values filter.'
    );
}

/**
 * Retrieves the appropriate condition group operator for the specified list-of-values report editor UI filter.
 * 
 * @param {{operator: string}} filter The list-of-values filter whose group operator will be retrieved.
 * 
 * @returns The appropriate condition group operator for the list-of-values filter.
 */
function getListGroupOperator({ operator }) {
    switch (operator) {
        case 'list':
            return ConditionGroupOperator.Or;
        case 'nlist':
            return ConditionGroupOperator.And;
    }

    throw new Error(
        'Could not determine the group operator for the filter because it is not a list-of-values filter.'
    );
}

/**
 * Indicates whether the specified report editor UI filter is a list-of-values filter.
 * 
 * @param {{operator: string}} filter The filter to test.
 * 
 * @returns A boolean indicating whether the filter is a list-of-values filter.
 */
function isList({ operator }) {
    return ['list', 'nlist'].includes(operator);
}

/**
 * Converts the specified list-of-values report editor UI filter to a condition group.
 * 
 * @param {unknown} filter The list-of-values filter to convert.
 * @param {number} order The group's order within its parent group.
 * 
 * @returns The new condition group representing the filter.
 */
function listOfValuesFilterToGroup(filter, order) {
    const conditionOperator = getListConditionOperator(filter);

    return {
        conditions: filter.listValues.map((value, i) => ({
            column: filter.columnId,
            operator: conditionOperator,
            order: i,
            value
        })),
        groups: [],
        operator: getListGroupOperator(filter),
        order
    };
}

/**
 * Produces a new condition group from the specified group with all unnecessary nested descendant groups removed.
 * 
 * A group is considered to be unnecessary if it contains only one child group and no child conditions.
 * 
 * @param {ConditionGroup} group The group to unwrap.
 * 
 * @returns {ConditionGroup} The new group with all unnecessary nested descendant groups removed.
 */
function unwrap({ conditions, groups, operator, order }) {
    if (conditions.length == 0 && groups.length == 1) {
        return unwrap(groups[0]);
    }

    return {
        conditions,
        groups: groups.map(unwrap),
        operator,
        order
    };
}
